import { fetchAuditEvents } from "@/domains/documents/history/documentHistoryAuditSource";
import { fetchCreatedDocuments } from "@/domains/documents/history/documentHistoryCreatedSource";
import {
  fetchScopedDocumentIndex,
  type ScopedDocumentIndex,
} from "@/domains/documents/history/documentHistoryScopedDocuments";
import {
  compareDocumentHistoryEntries,
  isNewerThanWatermark,
  type DocumentHistoryCursor,
  type DocumentHistoryEntry,
  type DocumentHistoryPage,
  type DocumentHistoryScope,
  type DocumentHistorySourceCursor,
} from "@/domains/documents/history/documentHistoryShared";

export { compareDocumentHistoryEntries } from "@/domains/documents/history/documentHistoryShared";
export type {
  DocumentHistoryAction,
  DocumentHistoryActor,
  DocumentHistoryChange,
  DocumentHistoryChangeField,
  DocumentHistoryCursor,
  DocumentHistoryEntry,
  DocumentHistoryPage,
} from "@/domains/documents/history/documentHistoryShared";

export const DOCUMENT_HISTORY_PAGE_SIZE = 25;

/**
 * How many source batches a single page may fetch before it returns what it
 * has. A batch can yield nothing at all — a project-scoped view discards every
 * audit event belonging to another project — so refilling would otherwise chain
 * serial round-trips without bound. Exhausting the budget is not an error: the
 * cursor still reports more to come, so the page ends short with "Load more"
 * left available and the next click resumes with a fresh budget.
 */
export const DOCUMENT_HISTORY_MAX_SOURCE_FETCHES = 8;

const HISTORY_SOURCES = ["documents", "audit"] as const;

interface FetchBudget {
  remaining: number;
}

interface HistorySources {
  scope: DocumentHistoryScope;
  /** Memoised: the audit source needs the same index for every batch of a page. */
  loadScopedDocuments: () => Promise<ScopedDocumentIndex | null>;
}

function historySources(scope: DocumentHistoryScope): HistorySources {
  let pending: Promise<ScopedDocumentIndex | null> | undefined;
  return { scope, loadScopedDocuments: () => (pending ??= fetchScopedDocumentIndex(scope)) };
}

function initialCursor(): DocumentHistoryCursor {
  return {
    documents: { offset: 0, done: false, buffered: [], snapshotAt: null, watermark: null },
    audit: { offset: 0, done: false, buffered: [], snapshotAt: null, watermark: null },
  };
}

async function refillSource(
  sources: HistorySources,
  source: (typeof HISTORY_SOURCES)[number],
  cursor: DocumentHistoryCursor,
  budget: FetchBudget
): Promise<void> {
  const current = cursor[source];
  if (current.buffered.length > 0 || current.done || budget.remaining <= 0) return;
  budget.remaining -= 1;

  const next =
    source === "documents"
      ? await fetchCreatedDocuments(sources.scope, current)
      : await fetchAuditEvents(sources.scope, current, await sources.loadScopedDocuments());

  cursor[source] = next;
  if (next.done || next.buffered.length > 0) return;
  return refillSource(sources, source, cursor, budget);
}

/**
 * A source that ran out of fetch budget before it could resolve a batch — an
 * empty buffer that is not yet exhausted. It may still be holding entries newer
 * than the other source's head, so what the other source may emit past it is
 * bounded by its watermark.
 */
function isStarved(source: DocumentHistorySourceCursor): boolean {
  return source.buffered.length === 0 && !source.done;
}

/**
 * The newest buffered entry across the sources, unless a starved source could
 * still be holding one newer: emitting past that would render entries the
 * *next* page finally resolves below older ones, and the reader has no way to
 * reconcile a timeline that jumps backwards.
 *
 * The bound is the starved source's watermark, not the source itself. A scope
 * too large to inline into the audit query leaves that source walking the whole
 * organisation's events, so it routinely spends the budget without resolving a
 * batch — refusing to emit at all then returned an empty page with "Load more"
 * still lit, click after click. Everything it has read past is still safe to
 * emit, so the creations above its watermark go out and the page fills.
 */
function takeNextEntry(cursor: DocumentHistoryCursor): DocumentHistoryEntry | undefined {
  const ready = HISTORY_SOURCES.filter((source) => cursor[source].buffered.length > 0);
  if (ready.length === 0) return undefined;

  const next = ready.reduce((best, source) =>
    compareDocumentHistoryEntries(cursor[best].buffered[0], cursor[source].buffered[0]) <= 0
      ? best
      : source
  );
  const head = cursor[next].buffered[0];
  const blocked = HISTORY_SOURCES.some(
    (source) => isStarved(cursor[source]) && !isNewerThanWatermark(head, cursor[source].watermark)
  );

  return blocked ? undefined : cursor[next].buffered.shift();
}

async function collectHistoryEntries(
  sources: HistorySources,
  cursor: DocumentHistoryCursor
): Promise<DocumentHistoryEntry[]> {
  const entries: DocumentHistoryEntry[] = [];
  const budget: FetchBudget = { remaining: DOCUMENT_HISTORY_MAX_SOURCE_FETCHES };
  while (entries.length < DOCUMENT_HISTORY_PAGE_SIZE) {
    await Promise.all(
      HISTORY_SOURCES.map((source) => refillSource(sources, source, cursor, budget))
    );
    const nextEntry = takeNextEntry(cursor);
    if (!nextEntry) break;
    entries.push(nextEntry);
  }
  return entries;
}

function sourceHasMore(cursor: DocumentHistoryCursor["documents"]): boolean {
  return cursor.buffered.length > 0 || !cursor.done;
}

/**
 * Fetch one merged history page from two authoritative sources:
 * document creation columns and immutable delete/restore audit events.
 * Mutable `modified_at` is deliberately not interpreted as user activity.
 */
export async function fetchDocumentHistoryPage(
  scope: DocumentHistoryScope,
  previousCursor: DocumentHistoryCursor | null
): Promise<DocumentHistoryPage> {
  const cursor = previousCursor ? structuredClone(previousCursor) : initialCursor();
  const entries = await collectHistoryEntries(historySources(scope), cursor);
  const hasMore = sourceHasMore(cursor.documents) || sourceHasMore(cursor.audit);

  return { entries, nextCursor: hasMore ? cursor : undefined };
}
